import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Divider } from '@ublend-npm/aulaui-core';
import SpaceItem from '../Home/widgets/common/SpaceItem';
import SpaceListSkeleton from '../Home/widgets/common/SpaceListSkeleton';
import InfiniteScrollTrigger from '../common/InfiniteScrollTrigger';

interface SpacesListProps {
  spaces: any[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  starredIds: Set<string>;
  handleStarToggle: (id: string) => void;
  hideEmptyState?: boolean;
}

const SKELETON_KEYS = [
  'skeleton-0',
  'skeleton-1',
  'skeleton-2',
  'skeleton-3',
  'skeleton-4',
];

const SpacesList: React.FC<SpacesListProps> = ({
  spaces,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  starredIds,
  handleStarToggle,
  hideEmptyState = false,
}) => {
  if (isLoading && spaces.length === 0) {
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        {SKELETON_KEYS.map((key, index) => (
          <React.Fragment key={key}>
            <SpaceListSkeleton />
            {index < 4 && <Divider />}
          </React.Fragment>
        ))}
      </Box>
    );
  }

  if (spaces.length === 0) {
    if (hideEmptyState) {
      return <Box minHeight="100px" />; // Retain some height to prevent visual collapse during exit animations
    }
    return (
      <Box p={4} textAlign="center">
        <Typography color="textSecondary">No spaces found.</Typography>
      </Box>
    );
  }

  const spacesCount = spaces.length;

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {spaces.map((space, index) => (
        <React.Fragment key={space.id}>
          <SpaceItem
            space={space}
            isStarred={starredIds.has(space.id)}
            onStarToggle={handleStarToggle}
          />
          {index < spacesCount - 1 && <Divider />}
        </React.Fragment>
      ))}

      {isLoadingMore && (
        <Box mt={1}>
          <SpaceListSkeleton />
        </Box>
      )}

      <InfiniteScrollTrigger
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={onLoadMore}
      />
    </Box>
  );
};

SpacesList.propTypes = {
  spaces: PropTypes.arrayOf(PropTypes.any).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingMore: PropTypes.bool.isRequired,
  hasMore: PropTypes.bool.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  starredIds: PropTypes.instanceOf(Set).isRequired,
  handleStarToggle: PropTypes.func.isRequired,
  hideEmptyState: PropTypes.bool,
};

export default SpacesList;
